import Auth from '@aws-amplify/auth';
import { SignInOpts } from '@aws-amplify/auth/lib-esm/types';
import { UserDetails } from '@coach-bot/data-access/shared';
import { AppView } from '@coach-bot/data-access/shared/enums/app-view.enum';
import { StoreName } from '@coach-bot/data-access/shared/enums/store-name.enum';
import { UserState, ViewState } from '@coach-bot/data-access/shared/interfaces/states';
import { User } from '@coach-bot/data-access/shared/models/user.model';
import { getLocalStorageKeyForStore } from '@coach-bot/data-access/shared/utils/local-storage';
import { isUserFromRestrictedDomain, isUserStudent } from '@coach-bot/data-access/shared/utils/permissions';
import { DashboardAction } from '@shared/extension';
import { CommonState } from '../store';
import { handleAuthStatusChanged, sendDashboardMessage } from '../utils';
import { Services } from './services.service';

const STORAGE_KEYS_TO_CLEAR_ON_SIGN_OUT: string[] = [
  getLocalStorageKeyForStore<UserState>('user', StoreName.UserStore),
  getLocalStorageKeyForStore<UserState>('userDetails', StoreName.UserStore),
  getLocalStorageKeyForStore<ViewState>('previousView', StoreName.ViewStore),
];

export class AuthService {
  constructor(private services: Services) {}

  async signIn(options: SignInOpts, notifyDashboard: boolean = true): Promise<User> {
    if (this.services.commonStore.isSignedIn$.value) {
      return;
    }

    await this.services.coachBotViewStore.setAuthLoading(true);
    let user: User;
    try {
      user = await Auth.signIn(options);
      const studentId: string | undefined = user.attributes['custom:studentID'];
      const isStudent: boolean = isUserStudent(user);
      if (!isStudent) {
        return user;
      }

      if (!!studentId) {
        await this.services.commonStore.setUserId(user.attributes['custom:userID']);
        await this.services.commonStore.setStudentId(studentId);
        await Promise.all([
          this.services.studentState.loadStudent(user.attributes['custom:userID']),
          this.services.studentState.loadStudentBalance(),
        ]);
        this.services.studentState.loadRecommendations();
        this.services.studentState.loadThirdPartyAppCredentialsList();
        this.services.coachBotUserStore.setForgotPasswordFormValue(null);
        this.services.coachBotUserStore.setSignInFormValue(null);
      }

      const { username: email, password } = options;

      let canOpenUi: boolean = false;

      await this.services.initialUserDetailsService
        .getUserDetailsByEmail(email)
        .then(async (userDetails: UserDetails) => {
          this.services.coachBotUserStore.setUserDetails(userDetails);
          canOpenUi = !isUserFromRestrictedDomain(userDetails);
        });

      this.services.coachBotUserStore.setUser(user);

      handleAuthStatusChanged(this.services, email);

      if (notifyDashboard) {
        await sendDashboardMessage(this.services, DashboardAction.ExtensionSignedIn, { username: email, password });
      }

      await this.services.coachBotViewStore.setView(canOpenUi ? AppView.Dashboard : AppView.Empty);
    } catch (err: unknown) {
      if (user) {
        await this.signOut();
      }
      throw err;
    } finally {
      await this.services.coachBotViewStore.setAuthLoading(false);
    }

    return user;
  }

  async signOut(notifyDashboard: boolean = true): Promise<void> {
    if (!this.services.commonStore.isSignedIn$.value) {
      return;
    }

    await chrome.storage.local.remove(STORAGE_KEYS_TO_CLEAR_ON_SIGN_OUT);
    await this.services.coachBotViewStore.setView(AppView.SignIn);
    handleAuthStatusChanged(this.services);
    try {
      await Auth.signOut();
    } catch (ex) {
      console.error(`An error occurred while signing out: ${ex}`);
    }
    await this.services.commonStore.setUserId(null);
    await this.services.commonStore.setStudentId(null);

    if (notifyDashboard) {
      await sendDashboardMessage(this.services, DashboardAction.ExtensionSignedOut);
    }
  }

  static async markUserAsSignedOut(): Promise<void> {
    const key: string = getLocalStorageKeyForStore<CommonState>('isSignedIn', StoreName.ServiceWorkerCommonStore);
    await chrome.storage.local.set({ [key]: false });
  }
}
